/**
 * <PageHeading> — large display title with optional single-word color accent.
 *
 * Pattern: PolicyOra headlines color exactly ONE word for focus.
 *   "Stay ahead of every regulation"   ← "regulation" in orange
 *
 * Usage:
 *   <PageHeading accentWord="regulation" accentTone="orange">
 *     Stay ahead of every regulation
 *   </PageHeading>
 *
 * If `accentWord` matches multiple words in the text, every occurrence is
 * highlighted. Matching is case-insensitive but preserves the original case.
 */
import React from 'react';
import styles from './PageHeading.module.css';

type AccentTone = 'orange' | 'blue' | 'teal' | 'red';
type Level = 'h1' | 'h2' | 'h3';

interface PageHeadingProps {
  children: string;
  accentWord?: string;
  accentTone?: AccentTone;
  level?: Level;
  size?: 'display' | 'h1' | 'h2' | 'h3';
  align?: 'left' | 'center';
  className?: string;
  /** The heading sits on an always-dark hero (e.g. a `--ds-dark` header band), whatever the theme. */
  onDark?: boolean;
}

export const PageHeading: React.FC<PageHeadingProps> = ({
  children,
  accentWord,
  accentTone = 'orange',
  level = 'h1',
  size = 'h1',
  align = 'left',
  className = '',
  onDark = false,
}) => {
  const Tag = level as keyof JSX.IntrinsicElements;

  const content = React.useMemo(() => {
    if (!accentWord) return children;
    // Split on the accent word, case-insensitive, preserve original casing
    const pattern = new RegExp(`(${escapeRegExp(accentWord)})`, 'gi');
    const parts = children.split(pattern);
    return parts.map((part, i) =>
      pattern.test(part) ? (
        <span key={i} className={styles[`accent_${accentTone}`]}>{part}</span>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    );
  }, [children, accentWord, accentTone]);

  return (
    <Tag
      className={`${styles.heading} ${styles[`size_${size}`]} ${styles[`align_${align}`]} ${onDark ? styles.onDark : ''} ${className}`}
    >
      {content}
    </Tag>
  );
};

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
